import React, { useState, useEffect } from 'react';
import { getTableBd } from '../../../config/functions';

export const suggestConversion = (localUnit, witsmlUnit) => {
    if (!localUnit || !witsmlUnit) return { factor: 1.0, text: 'Factor 1.0 (Directo)' };
    const l = localUnit.trim().toLowerCase();
    const w = witsmlUnit.trim().toLowerCase();

    if (l === w) return { factor: 1.0, text: 'Unidades idénticas (1.0)' };

    // Presión
    if ((l === 'bar' || l === 'bar-g' || l === 'barg') && w === 'psi') return { factor: 14.50377, text: '1 bar ≈ 14.5038 psi' };
    if (l === 'kpa' && w === 'psi') return { factor: 0.145038, text: '1 kPa ≈ 0.1450 psi' };
    if (l === 'psi' && w === 'psi') return { factor: 1.0, text: 'Misma unidad (1.0)' };

    // Longitud / Profundidad
    if (l === 'm' && w === 'ft') return { factor: 3.28084, text: '1 m ≈ 3.2808 ft' };
    if (l === 'in' && w === 'ft') return { factor: 0.083333, text: '1 in ≈ 0.0833 ft' };

    // Peso / Carga
    if ((l === 'ton' || l === 't' || l === 'ton-us' || l === 'tn' || l === 'klbs') && w === 'klbf') return { factor: 2.20462, text: '1 ton ≈ 2.2046 klbf' };
    if (l === 'klb' && w === 'klbf') return { factor: 1.0, text: '1 klb = 1.0 klbf' };
    if (l === 'lb' && w === 'klbf') return { factor: 0.001, text: '1 lb ≈ 0.001 klbf' };

    // Flujo
    if ((l === 'lpm' || l === 'l/min') && (w === 'gal/min' || w === 'gpm')) return { factor: 0.264172, text: '1 l/min ≈ 0.2642 gal/min' };
    if (l === 'm3/h' && (w === 'gal/min' || w === 'gpm')) return { factor: 4.40287, text: '1 m³/h ≈ 4.4029 gal/min' };

    // Volumen
    if ((l === 'm3' || l === 'm³') && w === 'bbl') return { factor: 6.28981, text: '1 m³ ≈ 6.2898 bbl' };

    return { factor: 1.0, text: 'Factor 1.0 o personalizado' };
};

export const SelectorNemotecnicoWitsml = ({
    witsmlNemo,
    witsmlDes,
    witsmlUmd,
    witsmlFactor,
    localUmd,
    variables = [],
    currentVarId,
    onChange
}) => {
    const [catalogo, setCatalogo] = useState([]);

    // Mapeo de nemotécnicos que ya están asociados a otra variable distinta a la actual
    const inUseMap = React.useMemo(() => {
        const map = {};
        if (Array.isArray(variables)) {
            variables.forEach(v => {
                if (v.vr_var_witsml_nemo && parseInt(v.nb_var_idn) !== parseInt(currentVarId)) {
                    map[v.vr_var_witsml_nemo] = v;
                }
            });
        }
        return map;
    }, [variables, currentVarId]);

    useEffect(() => {
        const cargarCatalogo = async () => {
            try {
                const data = await getTableBd('getwitsmldiccionario');
                if (Array.isArray(data)) {
                    setCatalogo(data);
                }
            } catch (err) {
                console.error('Error al cargar catálogo WITSML:', err);
            }
        };
        cargarCatalogo();
    }, []);

    const handleSelectChange = (e) => {
        const nemo = e.target.value;
        if (!nemo) {
            onChange({
                vr_var_witsml_nemo: null,
                vr_var_witsml_des: null,
                vr_var_witsml_umd: null,
                vr_var_witsml_conv_factor: 1.0
            });
            return;
        }

        const item = catalogo.find(c => c.vr_nemo === nemo);
        if (item) {
            const conversion = suggestConversion(localUmd, item.vr_unidad);
            onChange({
                vr_var_witsml_nemo: item.vr_nemo,
                vr_var_witsml_des: item.vr_descripcion,
                vr_var_witsml_umd: item.vr_unidad,
                vr_var_witsml_conv_factor: conversion.factor
            });
        }
    };

    const handleFactorChange = (val) => {
        onChange({
            vr_var_witsml_nemo: witsmlNemo,
            vr_var_witsml_des: witsmlDes,
            vr_var_witsml_umd: witsmlUmd,
            vr_var_witsml_conv_factor: val === '' ? '' : parseFloat(val)
        });
    };

    const conversionInfo = suggestConversion(localUmd, witsmlUmd);
    const variableAsociada = witsmlNemo ? inUseMap[witsmlNemo] : null;

    return (
        <>
            {/* Selector de Nemotécnico WitsML */}
            <div className="row g-3 justify-content-center">
                <div className="input-group mb-2">
                    <span className="input-group-text" id="basic-addon1">Nemotécnico WitsML</span>
                    <select
                        className={`form-select ${variableAsociada ? 'border-warning' : ''}`}
                        value={witsmlNemo || ''}
                        onChange={handleSelectChange}
                    >
                        <option value="">-- Sin Homologación WitsML --</option>
                        {catalogo.length > 0 && (
                            <>
                                <optgroup label="Work Over / Completamiento (26 variables)">
                                    {catalogo.filter(c => c.vr_tabla_origen && (c.vr_tabla_origen.includes('9') || c.vr_tabla_origen.toLowerCase().includes('work'))).map(c => {
                                        const used = inUseMap[c.vr_nemo];
                                        return (
                                            <option key={c.vr_nemo} value={c.vr_nemo}>
                                                {c.vr_nemo} — {c.vr_descripcion} ({c.vr_unidad}){used ? ` ⚠️ [En uso: ${used.vr_var_tag}]` : ''}
                                            </option>
                                        );
                                    })}
                                </optgroup>
                                <optgroup label="Perforación (14 variables)">
                                    {catalogo.filter(c => c.vr_tabla_origen && (c.vr_tabla_origen.includes('8') || c.vr_tabla_origen.toLowerCase().includes('perforac'))).map(c => {
                                        const used = inUseMap[c.vr_nemo];
                                        return (
                                            <option key={c.vr_nemo} value={c.vr_nemo}>
                                                {c.vr_nemo} — {c.vr_descripcion} ({c.vr_unidad}){used ? ` ⚠️ [En uso: ${used.vr_var_tag}]` : ''}
                                            </option>
                                        );
                                    })}
                                </optgroup>
                            </>
                        )}
                    </select>
                </div>
            </div>

            {/* Advertencia si el nemotécnico ya está en uso por otra variable */}
            {variableAsociada && (
                <div className="alert alert-warning py-2 px-3 mb-3 d-flex align-items-center" style={{ fontSize: '11.5px' }}>
                    <i className="fa-solid fa-triangle-exclamation me-2 fs-5 text-warning flex-shrink-0"></i>
                    <div>
                        <strong>Nemotécnico en uso:</strong> Actualmente asociado a <strong>{variableAsociada.vr_var_tag} ({variableAsociada.vr_var_nom})</strong>. 
                        Al guardar, se <strong>desasociará</strong> de dicha variable y se transferirá a esta.
                    </div>
                </div>
            )}

            {/* Campos condicionales cuando hay una variable WitsML seleccionada */}
            {witsmlNemo && (
                <>
                    <div className="row g-3 justify-content-center">
                        <div className="input-group mb-3">
                            <span className="input-group-text" id="basic-addon1">Descripción WitsML</span>
                            <input
                                type="text"
                                className="form-control"
                                value={witsmlDes || ''}
                                readOnly
                                disabled
                            />
                        </div>
                    </div>

                    <div className="row g-3 justify-content-center">
                        <div className="input-group mb-3">
                            <span className="input-group-text" id="basic-addon1">Unidad WitsML</span>
                            <input
                                type="text"
                                className="form-control"
                                value={witsmlUmd || ''}
                                readOnly
                                disabled
                            />
                        </div>
                    </div>

                    <div className="row g-3 justify-content-center">
                        <div className="input-group mb-2">
                            <span className="input-group-text" id="basic-addon1">Factor Conversión</span>
                            <input
                                type="number"
                                step="any"
                                className="form-control"
                                value={witsmlFactor !== undefined && witsmlFactor !== null ? witsmlFactor : 1.0}
                                onChange={(e) => handleFactorChange(e.target.value)}
                            />
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                title="Recalcular factor sugerido automáticamente"
                                onClick={() => handleFactorChange(conversionInfo.factor)}
                            >
                                Auto
                            </button>
                        </div>
                        <div className="px-1 mb-2">
                            <small className="text-muted d-block" style={{ fontSize: '11px' }}>
                                <i className="fa-solid fa-calculator me-1"></i>
                                {conversionInfo.text} &nbsp;|&nbsp; <code>WitsML = (Local) × {witsmlFactor || 1.0}</code>
                            </small>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};
